/**
 * CarrierPacketView — "this carrier's portal is yours to drive; here is
 * everything you will retype."
 *
 * Story 26739. Visual contract: artboard (4) in
 * `tasks/design/step-views.html`.
 *
 * Copy-ready rows, then the way into the portal, then somewhere to put the
 * confirmation when it comes back, then the promise that ora is still
 * watching. The rows are monospaced and each has its own copy button because
 * the Servicer is about to retype them into another window — this is the one
 * view whose job is transcription, not decision.
 *
 * NO DEAD PORTAL LINK (design decision D7). The link renders only when a URL
 * actually exists. A link that goes nowhere is worse than no link: it costs a
 * click and a page load to learn that ora had nothing.
 *
 * THE DROPZONE IS A SLOT. `evidencePanel` takes the shared
 * `CarrierEvidencePanel` — THE carrier-evidence component, the only one. This
 * view does not implement a second uploader; a second uploader is how the two
 * hosts ended up disagreeing about what "attached" meant.
 *
 * No Office.js, no Remix server import (design Constraint 20). Copying uses
 * `navigator.clipboard` when the browser has it and reports failure rather
 * than pretending — a copy button that silently does nothing is the failure
 * mode of every copy button ever shipped without one.
 */
import { useCallback, useRef, useState, type ReactNode } from "react";
import { StepViewFrame, identityLine } from "./StepViewFrame";
import { NEEDS_YOU_LABEL } from "../statusLabels";

export interface PacketRow {
  /** Short label — "Policy #", "Mortgagee", "Loan #". */
  label: string;
  /** The value, exactly as it should be typed into the carrier's form. */
  value: string;
}

/** The standing promise under the packet. */
export const KEEP_WATCHING_COPY = "We'll keep watching this run ›";

export interface CarrierPacketViewProps {
  clientName?: string | null;
  policyNumber?: string | null;
  /** The values the Servicer will retype. */
  packetRows?: readonly PacketRow[];
  /** The carrier portal. Omit / null renders NO link at all (D7). */
  portalUrl?: string | null;
  /** Link text. Defaults to the artboard's wording. */
  portalLabel?: string;
  /** The shared `CarrierEvidencePanel`, as a node. */
  evidencePanel?: ReactNode | null;
  /** Fired after a successful copy — telemetry only, never a decision. */
  onCopy?: (row: PacketRow) => void;
  /** "We'll keep watching this run ›" — omit for no hop. */
  onOpenRunRecord?: () => void;
  children?: ReactNode;
}

export function CarrierPacketView({
  clientName,
  policyNumber,
  packetRows,
  portalUrl,
  portalLabel = "Open the carrier portal ↗",
  evidencePanel,
  onCopy,
  onOpenRunRecord,
  children,
}: CarrierPacketViewProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const timerRef = useRef<number | null>(null);

  const handleCopy = useCallback(
    async (row: PacketRow) => {
      const clipboard = (
        globalThis as unknown as {
          navigator?: { clipboard?: { writeText?: (t: string) => Promise<void> } };
        }
      ).navigator?.clipboard;
      try {
        if (typeof clipboard?.writeText !== "function") {
          throw new Error("clipboard unavailable");
        }
        await clipboard.writeText(row.value);
        setCopyFailed(false);
        setCopied(row.label);
        onCopy?.(row);
        if (timerRef.current != null) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setCopied(null), 2000);
      } catch {
        // Say so. A copy button that quietly does nothing is worse than none:
        // the Servicer pastes whatever was on the clipboard before.
        setCopied(null);
        setCopyFailed(true);
      }
    },
    [onCopy],
  );

  const rows = packetRows ?? [];

  return (
    <StepViewFrame
      view="carrier_packet"
      eyebrow={NEEDS_YOU_LABEL}
      tone="attention"
      headline={identityLine(clientName, policyNumber)}
    >
      {rows.length > 0 ? (
        <div className="flex flex-col" data-testid="carrier-packet-rows">
          {rows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-[70px_1fr_auto] items-baseline gap-2 border-b border-primary-gray-light-200 py-[5px] font-mono text-[10.5px] text-primary-gray-light-900 last:border-b-0"
              data-testid="carrier-packet-row"
            >
              <span className="text-primary-gray-light-500">{row.label}</span>
              <span className="break-words">{row.value}</span>
              <button
                type="button"
                onClick={() => void handleCopy(row)}
                className="rounded-md border border-primary-gray-light-200 bg-base-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary-gray-light-700 hover:bg-primary-gray-light-100"
                aria-label={`Copy ${row.label}`}
                data-testid={`carrier-packet-copy-${row.label
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "")}`}
              >
                {copied === row.label ? "copied" : "copy"}
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {copyFailed ? (
        <p
          className="text-2xs text-primary-error-600"
          role="alert"
          data-testid="carrier-packet-copy-failed"
        >
          This browser would not let ora copy that — select the value and copy
          it by hand.
        </p>
      ) : null}

      {/* D7: only when a URL genuinely exists. */}
      {portalUrl ? (
        <a
          href={portalUrl}
          target="_blank"
          rel="noreferrer"
          className="w-fit border-b-2 border-sunrise text-[11.5px] font-semibold text-primary-gray-light-900"
          data-testid="carrier-packet-portal-link"
        >
          {portalLabel}
        </a>
      ) : null}

      {evidencePanel ?? null}

      {children}

      {onOpenRunRecord ? (
        <button
          type="button"
          onClick={onOpenRunRecord}
          className="w-fit border-t border-primary-gray-light-200 pt-2 text-left text-[10.5px] text-primary-gray-light-500 hover:text-primary-gray-light-800"
          data-testid="carrier-packet-run-record"
        >
          {KEEP_WATCHING_COPY}
        </button>
      ) : (
        <p
          className="border-t border-primary-gray-light-200 pt-2 text-[10.5px] text-primary-gray-light-500"
          data-testid="carrier-packet-watching"
        >
          {KEEP_WATCHING_COPY.replace(" ›", "")}
        </p>
      )}
    </StepViewFrame>
  );
}

export default CarrierPacketView;
